import { useCallback, useMemo } from 'react';
import * as yup from 'yup';
import { SUPPORTED_LEAGUES } from '../../../../../types';
import { useStringsContext } from '../../../../../contexts/strings';
import { PlayerPoll, PlayerPollKeys, PlayerPollOption } from '../../types';
import PollCard from '../poll-card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextInputWithValidate, {
  InputWithValidateProps,
} from '../../../../common/input/text-input-with-validate';
import { SelectOptions } from '../../../../common/input/select-with-validate';
import PollSelectedStaffClub from '../poll-select-staff-club';
import PollSelectedStaffLeague from '../poll-select-staff-league';

const titleKey: PlayerPollKeys = 'title';
const coachOptionsKey = 'coachOptions';

interface Props extends InputWithValidateProps {
  selectedPoll: PlayerPoll | null;
  selectedAbbrv: string;
}

const PollCoachDetailsCard = ({
  selectedPoll,
  selectedAbbrv,
  onValue,
  onValidation,
  hideValidationError,
}: Props) => {
  const { strings } = useStringsContext();
  const isLeague = selectedAbbrv ? selectedAbbrv in SUPPORTED_LEAGUES : false;

  const initialSelectedCoaches = useMemo(
    () => ({
      options:
        selectedPoll?.pollOptions
          ?.map((option) => option.value || (option.fogisId ? String(option.fogisId) : undefined))
          .filter((option): option is string => Boolean(option)) || [],
      optionsText: selectedPoll?.pollOptions?.map((option) => option.text || '') || [],
    }),
    [selectedPoll?.pollOptions]
  );

  const handleSelectedCoaches = useCallback(
    (selectedCoaches: SelectOptions | null) => {
      if (!onValue) {
        return;
      }

      const coachOptions: PlayerPollOption[] = (selectedCoaches?.options || []).map(
        (coachId, index) => {
          const coachText = selectedCoaches?.optionsText?.[index];
          return {
            id: index,
            value: String(coachId),
            text: coachText || String(coachId),
            type: 'staff',
          };
        }
      );

      onValue(coachOptionsKey, coachOptions);
    },
    [onValue]
  );

  return (
    <PollCard title={strings.polls.pollDetailsTitle}>
      <TextInputWithValidate
        name={titleKey}
        validation={yup.string().required(strings.validations.isRequired)}
        label={strings.polls.pollTitleLabel}
        value={selectedPoll?.[titleKey]}
        onValue={onValue}
        onValidation={onValidation}
        hideValidationError={hideValidationError}
      />
      <Stack spacing={2}>
        <Typography variant="body2-bold">{strings.polls.coachSelectTitle}</Typography>
        {isLeague ? (
          <PollSelectedStaffLeague
            selectedAbbrv={selectedAbbrv}
            initialSelectedStaff={initialSelectedCoaches}
            onSelectedStaff={handleSelectedCoaches}
            onValidation={onValidation}
            hideValidationError={hideValidationError}
          />
        ) : (
          <PollSelectedStaffClub
            selectedAbbrv={selectedAbbrv}
            initialSelectedStaff={initialSelectedCoaches}
            onSelectedStaff={handleSelectedCoaches}
            onValidation={onValidation}
            hideValidationError={hideValidationError}
          />
        )}
      </Stack>
    </PollCard>
  );
};

export default PollCoachDetailsCard;
